import { ALL_WIDGET_IDS, DEFAULT_BOOLEAN_SETTINGS } from "../constants";
import type {
    ExtensionSettings,
    ExtensionSettingsBooleans,
    ExtensionSettingsFieldKey,
    ExtensionSettingsPayload,
    OverviewWidgetItem,
} from "../types";

type ExtensionSettingsRawMap = Record<string, string>;

const FALLBACK_SETTINGS: ExtensionSettings = {
    ...DEFAULT_BOOLEAN_SETTINGS,
    overviewWidgets: ALL_WIDGET_IDS.map((id) => ({ id, isChecked: true })),
};

const BOOLEAN_KEYS: ExtensionSettingsFieldKey[] = Object.keys(FALLBACK_SETTINGS).filter(
    (k) => k !== "overviewWidgets",
) as ExtensionSettingsFieldKey[];

const WIDGETS_KEY = "companyOverviewWidgets";

export const serializeExtensionSettings = (settings: ExtensionSettings): ExtensionSettingsPayload => {
    const raw: Record<string, string> = {};

    for (const key of BOOLEAN_KEYS) {
        raw[key] = String(settings[key]);
    }

    raw[WIDGETS_KEY] = JSON.stringify(settings.overviewWidgets);

    return { value: JSON.stringify(raw) };
};

const parseBool = (value: string | undefined, fallback: boolean): boolean => {
    if (value === "true") {
        return true;
    }
    if (value === "false") {
        return false;
    }
    return fallback;
};

const isWidgetItem = (item: unknown): item is OverviewWidgetItem =>
    typeof item === "object" &&
    item !== null &&
    "id" in item &&
    typeof (item as Record<string, unknown>).id === "string" &&
    "isChecked" in item &&
    typeof (item as Record<string, unknown>).isChecked === "boolean";

const parseWidgets = (value: string | undefined): OverviewWidgetItem[] | null => {
    if (!value) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(value);
        if (!Array.isArray(parsed) || !parsed.every(isWidgetItem)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const deserializeExtensionSettings = (rawString: string): ExtensionSettings => {
    try {
        const raw = JSON.parse(rawString) as ExtensionSettingsRawMap;

        const booleans = Object.fromEntries(
            BOOLEAN_KEYS.map((key) => [key, parseBool(raw[key], FALLBACK_SETTINGS[key])]),
        ) as ExtensionSettingsBooleans;

        return {
            ...booleans,
            overviewWidgets: parseWidgets(raw[WIDGETS_KEY]) ?? FALLBACK_SETTINGS.overviewWidgets,
        };
    } catch {
        return FALLBACK_SETTINGS;
    }
};
